// BookingEditPage — single-vehicle edit of an existing booking.
//
// Reuses VehicleForm + the same field components as Create. Distinct from
// Create:
//   - Pre-populated from GET /api/v1/bookings/:enc_id
//   - PUT /api/v1/bookings/:enc_id (single-vehicle by server schema)
//   - No drafts (DECISION inheritance: drafts are book_in-only)
//   - useBlocker fires on unsaved-nav per the diary-results pattern
//   - Notification toggles default to OFF (UI_AUDIT §2.6 — must opt in)

import { useEffect, useMemo, useRef, useState } from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { Textarea } from '../../components/form/Textarea';
import { Select } from '../../components/form/Select';
import { DatePicker } from '../../components/form/DatePicker';
import { Checkbox } from '../../components/form/Checkbox';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { BookingLeftPanel } from '../../components/ui/BookingLeftPanel/BookingLeftPanel';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { DuplicateBookingModal } from '../../components/ui/DuplicateBookingModal/DuplicateBookingModal';
import { DriverHolidayWarning } from '../../components/ui/DriverHolidayWarning/DriverHolidayWarning';
import { useDriverHolidayCheck } from '../../hooks/useDriverHolidayCheck';
import { conflictingHolidays } from '../../lib/holidayPeriod';
import { CustomerNameAutocomplete } from './components/CustomerNameAutocomplete';
import { VehicleForm, type VehicleFormValue } from './components/VehicleForm';
import { useBookingForm, EMPTY_VEHICLE } from './hooks/useBookingForm';
import { useBookingDetail } from './hooks/useBookingDetail';
import { useBookingEdit } from './hooks/useBookingEdit';
import { useActiveDrivers } from './hooks/useDriversList';
import {
  useProducts,
  useWorkSources,
  usePaymentModes,
  useTimeSlots,
} from './hooks/useReferenceData';
import { queryKeys } from '../../api/queryKeys';
import type { SlotDayType, Timeframe } from '../../types/api';
import styles from './BookingCreatePage.module.css';

function slotDayType(dateStr: string): SlotDayType {
  if (!dateStr) return 'weekday';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return 'weekday';
  const day = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])).getDay();
  return day === 0 || day === 6 ? 'weekend' : 'weekday';
}

export function BookingEditPage(): JSX.Element {
  const { enc_id: encId } = useParams<{ enc_id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const detail = useBookingDetail(encId);

  const form = useBookingForm({
    initialBooking: detail.data ?? null,
  });
  const editMutation = useBookingEdit(encId, detail.data?.booking ?? null);

  const drivers = useActiveDrivers();
  const products = useProducts();
  const sources = useWorkSources();
  const paymentModes = usePaymentModes();
  const timeSlots = useTimeSlots(slotDayType(form.state.date));

  // Driver-holiday probe — same UX as BookingCreatePage. Non-blocking.
  const selectedDriver = useMemo(
    () => (drivers.data ?? []).find((d) => d.id === form.state.driver) ?? null,
    [drivers.data, form.state.driver],
  );
  const holidayCheck = useDriverHolidayCheck(
    selectedDriver?.uuid ?? null,
    form.state.date || null,
    form.state.date || null,
  );
  const [holidayWarningDismissed, setHolidayWarningDismissed] = useState(false);
  const lastConflictKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${selectedDriver?.uuid ?? ''}|${form.state.date}`;
    if (lastConflictKeyRef.current !== key) {
      lastConflictKeyRef.current = key;
      setHolidayWarningDismissed(false);
    }
  }, [selectedDriver?.uuid, form.state.date]);
  const conflictingHols = useMemo(
    () => conflictingHolidays(holidayCheck.holidays, form.state.date, form.state.timeframe),
    [holidayCheck.holidays, form.state.date, form.state.timeframe],
  );
  const showHolidayWarning =
    conflictingHols.length > 0 && !holidayWarningDismissed && form.state.date !== '';

  function focusDriverField() {
    const el = document.querySelector<HTMLSelectElement>(
      'select[aria-label="Driver"], [data-field="driver"] select',
    );
    el?.focus();
  }

  // Baseline snapshot — captured once, the first time the form has been
  // populated from the detail response. Subsequent renders compare the
  // current state's serialised form to the baseline so the dirty flag
  // never lights up just because of the initial seed.
  const baselineRef = useRef<string | null>(null);
  const [baselineCaptured, setBaselineCaptured] = useState(false);

  useEffect(() => {
    if (baselineCaptured) return;
    if (!detail.data) return;
    const v0 = form.state.vehicles[0];
    if (form.state.customer.fields.name === '' &&
        (v0 === undefined || v0.car_registration === '')) return;
    baselineRef.current = JSON.stringify(form.state);
    setBaselineCaptured(true);
  }, [baselineCaptured, detail.data, form.state]);

  const dirty =
    baselineCaptured &&
    baselineRef.current !== null &&
    baselineRef.current !== JSON.stringify(form.state);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!dirty) return false;
    if (editMutation.isSuccess) return false;
    return currentLocation.pathname !== nextLocation.pathname;
  });

  const driverOptions = (drivers.data ?? []).map((d) => ({
    value: String(d.id),
    label: d.name,
  }));
  const sourceOptions = (sources.data ?? []).map((s) => ({
    value: String(s.id),
    label: s.name,
  }));
  const paymentOptions = (paymentModes.data ?? []).map((p) => ({
    value: String(p.id),
    label: p.name,
  }));

  if (detail.isLoading) {
    return (
      <div className={styles.page}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '2rem' }}>
          <Spinner size="md" />
          <span>Loading booking…</span>
        </div>
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Edit Booking" subtitle="Could not load this booking." />
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>
    );
  }

  const original = detail.data.booking;
  const fields = form.state.customer.fields;

  async function handleSubmit() {
    await editMutation.mutateAsync(form.state);
    // Reset baseline so the post-save state isn't considered dirty.
    baselineRef.current = JSON.stringify(form.state);
    if (encId) {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.bookings.detail(encId),
      });
    }
    navigate(`/collections/bookings/${encodeURIComponent(encId ?? '')}`);
  }

  return (
    <div className={styles.layout}>
      <BookingLeftPanel
        selectedCustomer={form.state.customer.customer}
        onCustomerSelect={(c) => form.setCustomer(c)}
      />
      <div className={styles.main}>
      <PageHeader
        title={`Edit Booking — ${original.reference}`}
        subtitle={`${original.car_make_model} · ${original.car_registration}`}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate(-1)}
            disabled={editMutation.isPending}
          >
            Cancel
          </Button>
        }
      />

      <Card title="Collection details">
        <div className={styles.grid2}>
          <DatePicker
            label="Collection date"
            required
            value={form.state.date}
            onChange={(e) => form.setField('date', e.target.value)}
          />
          <Select
            label="Time slot"
            required
            options={(timeSlots.data ?? []).map((t) => ({ value: t.label, label: t.label }))}
            value={form.state.timeframe}
            onChange={(e) => form.setField('timeframe', e.target.value as Timeframe)}
          />
          <Select
            label="Driver"
            options={driverOptions}
            placeholder={drivers.isLoading ? 'Loading…' : '—'}
            value={form.state.driver !== null ? String(form.state.driver) : ''}
            onChange={(e) =>
              form.setField('driver', e.target.value === '' ? null : Number(e.target.value))
            }
          />
          <Select
            label="Work source"
            options={sourceOptions}
            placeholder={
              sources.isLoading
                ? 'Loading…'
                : sourceOptions.length === 0
                  ? 'No options — leave blank'
                  : '—'
            }
            value={form.state.source !== null ? String(form.state.source) : ''}
            onChange={(e) =>
              form.setField('source', e.target.value === '' ? null : Number(e.target.value))
            }
          />
          <Select
            label="Payment mode"
            options={paymentOptions}
            placeholder={
              paymentModes.isLoading
                ? 'Loading…'
                : paymentOptions.length === 0
                  ? 'No options — leave blank'
                  : '—'
            }
            value={form.state.payment_mode !== null ? String(form.state.payment_mode) : ''}
            onChange={(e) =>
              form.setField(
                'payment_mode',
                e.target.value === '' ? null : Number(e.target.value),
              )
            }
          />
          <div />
        </div>

        <div className={styles.collectionAddress}>
          <Checkbox
            checked={form.state.use_customer_address}
            onChange={(e) => form.setField('use_customer_address', e.target.checked)}
            label="Collection address is the same as the customer address"
          />
          {!form.state.use_customer_address ? (
            <div className={styles.grid2}>
              <Input
                label="Collection address"
                value={form.state.collection_address}
                onChange={(e) => form.setField('collection_address', e.target.value)}
              />
              <Input
                label="Collection postcode"
                value={form.state.collection_postcode}
                onChange={(e) => form.setField('collection_postcode', e.target.value)}
              />
            </div>
          ) : null}
        </div>
      </Card>

      <Card title="Vehicle">
        <VehicleForm
          index={0}
          total={1}
          value={form.state.vehicles[0] ?? (EMPTY_VEHICLE as VehicleFormValue)}
          products={products.data ?? []}
          productsLoading={products.isLoading}
          duplicateWarning={form.duplicateWarnings[0]}
          onChange={(next) => form.setVehicle(0, next)}
          onRegBlur={(reg) => void form.checkDuplicate(0, reg)}
        />
      </Card>

      <Card title="Customer">
        <div className={styles.grid2}>
          <CustomerNameAutocomplete
            value={fields.name ?? ''}
            onChange={(v) => form.setCustomerField('name', v)}
            onSelectCustomer={(c) => form.setCustomer(c)}
            linkedCustomer={form.state.customer.customer}
            onClearLinked={() => form.setNewCustomerMode(true)}
            label="Name"
            required
          />
          <Input
            label="Phone"
            required
            variant="tel"
            value={fields.phone ?? ''}
            onChange={(e) => form.setCustomerField('phone', e.target.value)}
          />
          <Input
            label="Email"
            variant="email"
            value={fields.email ?? ''}
            onChange={(e) => form.setCustomerField('email', e.target.value)}
          />
          <Input
            label="Phone (alt)"
            variant="tel"
            value={fields.phone2 ?? ''}
            onChange={(e) => form.setCustomerField('phone2', e.target.value)}
          />
          <Input
            label="Address"
            required
            value={fields.address ?? ''}
            onChange={(e) => form.setCustomerField('address', e.target.value)}
          />
          <Input
            label="Postcode"
            required
            value={fields.postcode ?? ''}
            onChange={(e) =>
              form.setCustomerField('postcode', e.target.value.toUpperCase())
            }
          />
        </div>
      </Card>

      <Card title="Notes">
        <Textarea
          rows={3}
          autoResize
          value={form.state.notes}
          onChange={(e) => form.setField('notes', e.target.value)}
          aria-label="Booking notes"
        />
      </Card>

      <Card title="Send Update Notifications">
        <div className={styles.toggleRow}>
          <Checkbox
            checked={form.state.send_email}
            onChange={(e) => form.setField('send_email', e.target.checked)}
            label="Send Email Notification"
          />
          <Checkbox
            checked={form.state.send_sms}
            onChange={(e) => form.setField('send_sms', e.target.checked)}
            label="Send SMS Notification"
          />
        </div>
      </Card>

      <Card title="Reminders">
        <div className={styles.toggleRow}>
          <Checkbox
            checked={form.state.reminder_email}
            onChange={(e) => form.setField('reminder_email', e.target.checked)}
            label="Email reminder (day before)"
          />
          <Checkbox
            checked={form.state.reminder_sms}
            onChange={(e) => form.setField('reminder_sms', e.target.checked)}
            label="SMS reminder (day before)"
          />
        </div>
      </Card>

      <div className={styles.submitBar}>
        <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8125rem' }}>
          Notify the customer about these booking changes — defaults off (UI_AUDIT §2.6).
        </span>
        <Button
          variant="primary"
          onClick={() => void handleSubmit()}
          loading={editMutation.isPending}
        >
          Update booking
        </Button>
      </div>

      {/* Duplicate-registration modal — opens on reg blur when matches found */}
      <DuplicateBookingModal
        open={form.duplicateModal !== null}
        registration={form.duplicateModal?.registration ?? ''}
        bookings={form.duplicateModal?.bookings ?? []}
        pending={form.duplicateModal?.pending ?? []}
        onContinue={form.dismissDuplicateModal}
        onClear={() => {
          if (form.duplicateModal) {
            form.clearVehicleRegistration(form.duplicateModal.index);
          }
        }}
        onViewMatch={(m) => {
          if (m.source === 'bookings') {
            navigate(`/collections/bookings/${encodeURIComponent(m.enc_id)}`);
          } else {
            navigate(`/collections/pending?highlight=${encodeURIComponent(m.enc_id)}`);
          }
        }}
      />

      {/* Unsaved-changes guard */}
      <ConfirmModal
        open={blocker.state === 'blocked'}
        onCancel={() => blocker.reset?.()}
        onConfirm={() => blocker.proceed?.()}
        title="Discard changes?"
        confirmLabel="Leave without saving"
        cancelLabel="Stay on page"
        variant="danger"
        message="You have unsaved changes to this booking. Leave without saving?"
      />

      {/* Driver-holiday conflict warning — same as BookingCreatePage. */}
      <DriverHolidayWarning
        isOpen={showHolidayWarning}
        onClose={() => setHolidayWarningDismissed(true)}
        onContinueAnyway={() => setHolidayWarningDismissed(true)}
        onChangeDriverOrDate={() => {
          setHolidayWarningDismissed(true);
          focusDriverField();
        }}
        driverName={selectedDriver?.name ?? ''}
        bookingDate={form.state.date}
        holidays={conflictingHols}
      />
      </div>
    </div>
  );
}
